import { CommutingTripSummary, PlaceIcon, useUnits } from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import {
  Box,
  CardActions,
  CardContent,
  IconButton,
  Card as MuiCard,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { AppContext } from '../../app/context';
import { Button, Card, EditIcon, RemoveIcon } from '../../components';
import { FlagIcon } from '../../components/icons/flag';
import useScrollToAnchor from '../../hooks/anchor';

import RemoveJourneyDialog from './remove-journey-dialog';

function JourneysCard({
  multimodalCommutingTrips,
}: {
  multimodalCommutingTrips: CommutingTripSummary[] | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  useScrollToAnchor();

  if (multimodalCommutingTrips && multimodalCommutingTrips.length === 0) return <></>;

  return (
    <Card
      disableDivider
      header={
        <Box display="flex" flexDirection="column" gap={3}>
          <Typography fontSize="1.1rem" fontWeight={600}>
            {t('companies.pages.admin.company.data.journeys_card.title')}
          </Typography>
          <Typography variant="body2">
            {t('companies.pages.admin.company.data.journeys_card.description')}
          </Typography>
        </Box>
      }
      id="journeys"
    >
      <Box display="flex" flexDirection="column" gap={3} padding={3}>
        {multimodalCommutingTrips ? (
          multimodalCommutingTrips.map((commutingTrip) => (
            <JourneyCard commutingTrip={commutingTrip} key={commutingTrip.id} />
          ))
        ) : (
          <JourneyCard />
        )}
        <Box>
          <Button
            color="primary"
            component={Link}
            disabled={!multimodalCommutingTrips}
            size="medium"
            startIcon={<Add />}
            to="/profile/new-journey"
            variant="text"
          >
            {t('companies.pages.admin.company.data.journeys_card.actions.add_journey')}
          </Button>
        </Box>
      </Box>
    </Card>
  );
}

export function JourneyCard({
  disableActions,
  commutingTrip,
}: {
  commutingTrip?: CommutingTripSummary;
  disableActions?: boolean;
}): React.JSX.Element {
  const [removeDialogOpen, openRemoveDialog] = useState(false);
  const {
    user: { commutingTrips },
    actions: { setUserCommutingTrips },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const theme = useTheme();

  function handleRemoveDialogClose(removed?: boolean) {
    openRemoveDialog(false);

    if (removed) {
      setTimeout(() => {
        if (commutingTrips && commutingTrip)
          setUserCommutingTrips([...commutingTrips].filter(({ id }) => id !== commutingTrip.id));
      }, theme.transitions.duration.leavingScreen);
    }
  }

  return (
    <>
      <MuiCard
        sx={{ alignItems: 'flex-start', borderRadius: 4, display: 'flex', flexDirection: 'row' }}
        variant="outlined"
      >
        <JourneysCardContent commutingTrip={commutingTrip} />
        {!disableActions && (
          <CardActions sx={{ flexShrink: 0 }}>
            {commutingTrip ? (
              <>
                <Tooltip placement="top" title={t('commons.actions.update')}>
                  <div>
                    <IconButton
                      color="primary"
                      component={Link}
                      size="small"
                      to={`/profile/journeys/${commutingTrip.id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip placement="top" title={t('commons.actions.remove')}>
                  <div>
                    <IconButton color="error" onClick={() => openRemoveDialog(true)} size="small">
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </div>
                </Tooltip>
              </>
            ) : (
              <>
                <Skeleton height={30} variant="circular" width={30} />
                <Skeleton height={30} variant="circular" width={30} />
              </>
            )}
          </CardActions>
        )}
      </MuiCard>
      {commutingTrip && (
        <RemoveJourneyDialog
          commutingTrip={commutingTrip}
          onClose={handleRemoveDialogClose}
          open={removeDialogOpen}
        />
      )}
    </>
  );
}

export function JourneysCardContent({
  size,
  directBadgeBgColor,
  commutingTrip,
}: {
  commutingTrip: CommutingTripSummary | undefined;
  directBadgeBgColor?: string;
  size?: 'small';
}): React.JSX.Element {
  const { t } = useTranslation();
  const { toDistance } = useUnits();

  return (
    <CardContent
      sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', flexGrow: 1, gap: 3 }}
    >
      {/* <Box flexShrink={0}>
        <Skeleton height={128} sx={{ borderRadius: 2 }} variant="rounded" width={160} />
      </Box> */}
      <Box display="flex" flexDirection="column" gap={2}>
        <Box alignItems="flex-start" display="flex" flexDirection="column" gap={1}>
          <Typography fontWeight={700} variant={size === 'small' ? 'body2' : 'body1'}>
            {commutingTrip ? commutingTrip.title : <Skeleton variant="text" width={200} />}{' '}
            <Typography
              color="textSecondary"
              component="span"
              variant={size === 'small' ? 'caption' : 'body2'}
            >
              {commutingTrip ? (
                <>({toDistance(commutingTrip.bicycleDistance)})</>
              ) : (
                <Skeleton variant="text" width={50} />
              )}
            </Typography>
          </Typography>
          {commutingTrip ? (
            commutingTrip.bicyclePortions.length === 1 ? (
              <Box
                alignItems="center"
                bgcolor={directBadgeBgColor || '#ECFBF4'}
                borderRadius={2}
                display="flex"
                height={24}
                paddingX={1}
              >
                <Typography color="#005A44" lineHeight={1} variant="caption">
                  {t('companies.pages.admin.company.data.journeys_card.direct_route')}
                </Typography>
              </Box>
            ) : (
              <Box
                alignItems="center"
                bgcolor="#EEE8FD"
                borderRadius={2}
                display="flex"
                height={24}
                paddingX={1}
              >
                <Typography color="#6236BD" lineHeight={1} variant="caption">
                  {t('companies.pages.admin.company.data.journeys_card.bicycle_portions', {
                    count: commutingTrip.bicyclePortions.length,
                  })}
                </Typography>
              </Box>
            )
          ) : (
            <Skeleton height={24} sx={{ borderRadius: 2 }} variant="rounded" width={100} />
          )}
        </Box>
        <Box display="flex" flexDirection="column" gap={size === 'small' ? 0 : 1}>
          <Box alignItems="center" display="flex" gap={1}>
            <PlaceIcon sx={{ height: 16, width: 16 }} />
            <Typography variant={size === 'small' ? 'caption' : 'body2'}>
              {commutingTrip ? (
                commutingTrip.bicyclePortions[0]?.start.address || commutingTrip.home.address
              ) : (
                <Skeleton variant="text" width={200} />
              )}
            </Typography>
          </Box>
          <Box alignItems="center" display="flex" gap={1}>
            <FlagIcon sx={{ height: 16, width: 16 }} />
            <Typography variant={size === 'small' ? 'caption' : 'body2'}>
              {commutingTrip ? (
                commutingTrip.bicyclePortions[commutingTrip.bicyclePortions.length - 1]?.end
                  .address || commutingTrip.work.address
              ) : (
                <Skeleton variant="text" width={200} />
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    </CardContent>
  );
}

export default JourneysCard;
